import ResourceManager from "../../components/ResourceManager/ResourceManager"
import { StringMap } from "../../types/common"
import { useI18n } from "../../hooks"
import { useOptions } from "../../options/hooks/use-options"
import { useTodoMutations } from "../hooks/use-todo-mutations"
import { TodoViewModel } from "../api/todo"
import { uniq } from "lodash"

const TodoWorkspaceWidget = ({ todo, boardId, columnPosition }: TodoWorkspaceWidgetProps) => {
  const translations = useTranslations()
  const { workspaceOptions } = useOptions(todo.organisation?.id)
  const { updateWorkspaces } = useTodoMutations()

  const onAddWorkspace = (workspaceIdsToAdd: string[]) => {
    const workspaces = uniq([...todo.workspaces.map((w) => w.id), ...workspaceIdsToAdd]).map((id) => {
      const ws = workspaceOptions.find((w) => w.id === id)
      return ws ? { id: ws.id, name: ws.name } : { id, name: "" }
    })
    updateWorkspaces(todo.id, { workspaces, boardId, columnPosition })
  }

  const onRemoveWorkspace = (workspaceId: string) => {
    const workspaces = todo.workspaces.filter((w) => w.id !== workspaceId)
    updateWorkspaces(todo.id, { workspaces, boardId, columnPosition })
  }

  const sortedWorkspaceOptions = workspaceOptions
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  const sortedWorkspaces = todo.workspaces
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedWorkspaceOptions}
      resources={sortedWorkspaces}
      onAddResources={onAddWorkspace}
      onRemoveResource={onRemoveWorkspace}
      canAddResource={todo.canUpdate}
      canRemoveResource={todo.canUpdate}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations: t } = useI18n("todo")
  const translations = (t?.workspaceWidget || {}) as StringMap
  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
    updateErrorMessage = defaults.updateErrorMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
    updateErrorMessage,
  }
}

const defaultTranslations = {
  title: "Workspaces",
  emptyOptionsMessage: "No workspaces in this organisation",
  emptyResourcesMessage: "Todo is not connected to any workspace",
  updateErrorMessage: "Failed to update workspaces",
}

type Translations = typeof defaultTranslations

type TodoWorkspaceWidgetProps = {
  todo: TodoViewModel
  workspaceId?: string
  boardId?: string
  columnPosition?: number
}

export default TodoWorkspaceWidget
